/**
 * ChurchPledgesTab — the assembly's pledge book, now joined to the campaigns.
 *
 * The form used to offer a hardcoded list of categories and a free-text
 * purpose, because the record behind it had no campaign field at all. A
 * secretary could write "International Building Campaign" in a box and the
 * campaign would never hear about it. Now the dropdown is fed by the campaigns
 * this assembly may actually pledge to, and a payment recorded here moves the
 * campaign's own total — no second entry in the Campaigns workspace, and no
 * campaign administration permission needed to do it.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Loader2, HandCoins, Receipt, Undo2, Search, Megaphone, Printer, FileText, Trophy,
} from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import api, { getErrorMessage } from '@/lib/api';
import ReceiptDialog from './ReceiptDialog';
import { GiverStatementDialog } from './GiverStatement';
import AssemblyLeaderboard from '@/components/campaigns/AssemblyLeaderboard';
import ExchangeRateRequests from '@/components/finance/ExchangeRateRequests';
import CurrencySelect from '@/components/CurrencySelect';

const CATEGORIES = ['general', 'building', 'missions', 'youth', 'welfare', 'tithe'];
const METHODS = ['cash', 'bank_transfer', 'mobile_money', 'card', 'cheque'];
const money = (n, ccy) => `${ccy} ${Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatusBadge = ({ status }) => {
  const tone = { fulfilled: 'bg-emerald-600', partial: 'bg-amber-500',
                 pending: 'bg-slate-500', cancelled: 'bg-rose-600' }[status] || 'bg-slate-500';
  return <Badge className={`${tone} text-white`} data-testid={`pledge-status-${status}`}>{status}</Badge>;
};

export default function ChurchPledgesTab({ churchId, homeCurrency = 'USD' }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [paying, setPaying] = useState(null);
  const [statementFor, setStatementFor] = useState(null);
  const [standingsFor, setStandingsFor] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['assembly-pledges', churchId, q],
    queryFn: () => api.get(`/church/${churchId}/pledges`, { params: q ? { q } : {} })
      .then((r) => r.data),
    enabled: !!churchId,
  });

  const { data: campaignData } = useQuery({
    queryKey: ['pledge-campaigns', churchId],
    queryFn: () => api.get(`/church/${churchId}/pledge-campaigns`).then((r) => r.data),
    enabled: !!churchId,
  });
  const campaigns = campaignData?.campaigns || [];

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['assembly-pledges', churchId] });
    // A payment moves the standings, the receipt and the giver's statement too.
    // Leaving those cached showed a reversed payment with no stamp on it.
    qc.invalidateQueries({ queryKey: ['assembly-leaderboard'] });
    qc.invalidateQueries({ queryKey: ['receipt'] });
    qc.invalidateQueries({ queryKey: ['giver-statement'] });
    setPaying(null);
  };

  const pledges = data?.pledges || [];
  const byCurrency = data?.by_currency || {};

  return (
    <div className="space-y-4" data-testid="church-pledges-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[12rem] flex-1">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-8"
                 placeholder="Search by name or phone" data-testid="pledge-search" />
        </div>
        <Button onClick={() => setCreating(true)} data-testid="pledge-add">
          <Plus className="mr-1.5 h-4 w-4" />New pledge
        </Button>
      </div>

      {Object.keys(byCurrency).length ? (
        <div className="grid gap-2 sm:grid-cols-3" data-testid="pledge-totals">
          {Object.entries(byCurrency).map(([ccy, t]) => (
            <Card key={ccy}><CardContent className="p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{ccy}</p>
              <p className="mt-1 text-sm text-slate-700">
                Pledged <strong>{money(t.pledged, ccy)}</strong>
              </p>
              <p className="text-sm text-slate-700">Received <strong>{money(t.received, ccy)}</strong></p>
              <p className="text-sm text-slate-700">Outstanding <strong>{money(t.outstanding, ccy)}</strong></p>
            </CardContent></Card>
          ))}
        </div>
      ) : null}

      {isLoading ? (
        <p className="flex items-center gap-2 p-4 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />Loading pledges…
        </p>
      ) : pledges.length ? (
        <div className="space-y-2">
          {pledges.map((p) => (
            <Card key={p.id} data-testid={`pledge-row-${p.id}`}>
              <CardContent className="flex flex-wrap items-center gap-3 p-3">
                <div className="min-w-[10rem] flex-1">
                  <p className="font-semibold text-slate-900">{p.donor_name}</p>
                  <p className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    {p.campaign_title ? (
                      <span className="inline-flex items-center gap-1 text-amber-700"
                            data-testid={`pledge-campaign-${p.id}`}>
                        <Megaphone className="h-3 w-3" />{p.campaign_title}
                      </span>
                    ) : <span>{p.category || 'general'} · this assembly</span>}
                    {p.purpose ? <span>· {p.purpose}</span> : null}
                  </p>
                </div>
                <div className="text-right text-sm">
                  <p className="text-slate-900">{money(p.amount, p.currency)}</p>
                  <p className="text-xs text-slate-500">
                    paid {money(p.paid_amount, p.currency)} · outstanding{' '}
                    <strong data-testid={`pledge-outstanding-${p.id}`}>
                      {money(p.remaining_amount, p.currency)}
                    </strong>
                  </p>
                </div>
                <StatusBadge status={p.status} />
                {p.member_id ? (
                  <Button size="sm" variant="ghost" onClick={() => setStatementFor(p)}
                          data-testid={`pledge-statement-${p.id}`}>
                    <FileText className="mr-1.5 h-4 w-4" />Statement
                  </Button>
                ) : null}
                <Button size="sm" variant="outline" onClick={() => setPaying(p)}
                        disabled={p.status === 'cancelled'}
                        data-testid={`pledge-pay-${p.id}`}>
                  <HandCoins className="mr-1.5 h-4 w-4" />Record payment
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card><CardContent className="p-8 text-center text-sm text-slate-500"
                           data-testid="pledge-empty">
          No pledges recorded yet.
        </CardContent></Card>
      )}

      <ExchangeRateRequests />

      <Standings churchId={churchId} pledges={pledges} value={standingsFor}
                 onChange={setStandingsFor} />

      {creating ? (
        <PledgeDialog churchId={churchId} campaigns={campaigns} homeCurrency={homeCurrency}
                      onClose={() => setCreating(false)} onSaved={refresh} />
      ) : null}
      {paying ? (
        <PaymentDialog churchId={churchId} pledge={paying}
                       onClose={() => setPaying(null)} onSaved={refresh} />
      ) : null}
      {statementFor ? (
        <GiverStatementDialog churchId={churchId} memberId={statementFor.member_id}
                              name={statementFor.donor_name}
                              onClose={() => setStatementFor(null)} />
      ) : null}
    </div>
  );
}

/** How this assembly is doing beside the others, on an appeal it has joined. */
const Standings = ({ churchId, pledges, value, onChange }) => {
  const joined = useMemo(() => {
    const seen = new Map();
    (pledges || []).forEach((p) => {
      if (p.campaign_id && !seen.has(p.campaign_id)) seen.set(p.campaign_id, p.campaign_title);
    });
    return Array.from(seen, ([id, title]) => ({ id, title }));
  }, [pledges]);

  if (!joined.length) return null;

  return (
    <Card data-testid="assembly-standings">
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Trophy className="h-4 w-4 text-amber-500" />How we are doing
          </p>
          <Select value={value || joined[0].id} onValueChange={onChange}>
            <SelectTrigger className="h-8 w-[14rem] text-xs" data-testid="standings-campaign">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {joined.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.title || 'Appeal'}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <AssemblyLeaderboard campaignId={value || joined[0].id} churchId={churchId}
                             variant="office" />
      </CardContent>
    </Card>
  );
};

const PledgeDialog = ({ churchId, campaigns, homeCurrency, onClose, onSaved }) => {
  const [form, setForm] = useState({
    donor_name: '', donor_phone: '', campaign_id: 'none', category: 'general',
    purpose: '', amount: '', currency: homeCurrency, expected_date: '', notes: '',
  });

  const save = useMutation({
    mutationFn: () => api.post(`/church/${churchId}/pledges`, {
      ...form,
      campaign_id: form.campaign_id === 'none' ? '' : form.campaign_id,
      amount: Number(form.amount),
    }),
    onSuccess: (r) => {
      toast.success('Pledge recorded');
      if (r.data?.note) toast.info(r.data.note);
      onSaved();
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not save the pledge')),
  });

  const valid = form.donor_name.trim().length > 1 && Number(form.amount) > 0;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="pledge-dialog">
        <DialogHeader><DialogTitle>New pledge</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={form.donor_name} data-testid="pledge-name"
                   onChange={(e) => setForm((f) => ({ ...f, donor_name: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Towards</Label>
            <Select value={form.campaign_id}
                    onValueChange={(v) => setForm((f) => ({
                      ...f,
                      campaign_id: v,
                      // A campaign appeal is counted in the campaign's own
                      // currency unless the secretary says otherwise, so the
                      // assembly's home currency does not quietly relabel it.
                      currency: (campaigns.find((c) => c.id === v)?.currency
                                 || homeCurrency).toUpperCase(),
                    }))}>
              <SelectTrigger data-testid="pledge-campaign-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">This assembly's own work</SelectItem>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`pledge-campaign-option-${c.id}`}>
                    {c.title}{c.owner_name ? ` · ${c.owner_name}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!campaigns.length ? (
              <p className="mt-1 text-xs text-slate-500">
                No campaigns are open to this assembly at the moment.
              </p>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount</Label>
              <Input type="number" value={form.amount} data-testid="pledge-amount"
                     onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Currency</Label>
              <CurrencySelect
                value={form.currency}
                homeCurrency={homeCurrency}
                showHomeHint={false}
                testId="pledge-currency"
                onChange={(c) => setForm((f) => ({ ...f, currency: c }))}
              />
            </div>
          </div>
          {form.campaign_id === 'none' ? (
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={form.category}
                      onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="pledge-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={form.donor_phone} data-testid="pledge-phone"
                     onChange={(e) => setForm((f) => ({ ...f, donor_phone: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Expected by</Label>
              <Input type="date" value={form.expected_date} data-testid="pledge-expected"
                     onChange={(e) => setForm((f) => ({ ...f, expected_date: e.target.value }))} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}
                  data-testid="pledge-save">
            {save.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Save pledge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PaymentDialog = ({ churchId, pledge, onClose, onSaved }) => {
  const [form, setForm] = useState({
    amount: '', currency: pledge.currency, payment_method: 'cash',
    reference: '', notes: '',
  });
  const [receiptFor, setReceiptFor] = useState(null);
  // One key per dialog: pressing Save twice is the same payment, and the
  // server returns the first result rather than taking the money again.
  const key = useMemo(() => `${pledge.id}-${Date.now()}-${Math.random().toString(36).slice(2)}`, [pledge.id]);

  // What this will be worth against the pledge, shown before it is confirmed.
  const differentCurrency = form.currency && form.currency !== pledge.currency;
  const { data: quote } = useQuery({
    queryKey: ['payment-quote', pledge.id, form.amount, form.currency],
    queryFn: () => api.get(`/church/${churchId}/pledges/${pledge.id}/payment-quote`,
      { params: { amount: Number(form.amount), currency: form.currency } })
      .then((r) => r.data),
    enabled: !!(differentCurrency && Number(form.amount) > 0),
    retry: false,
  });

  const pay = useMutation({
    mutationFn: () => api.post(`/church/${churchId}/pledges/${pledge.id}/payments`,
      { ...form, amount: Number(form.amount), idempotency_key: key }),
    onSuccess: (r) => {
      toast.success(r.data?.duplicate
        ? 'That payment was already recorded — nothing taken twice'
        : `Received. Outstanding: ${money(r.data.pledge.remaining_amount, r.data.pledge.currency)}`);
      onSaved();
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not record the payment')),
  });

  const reverse = useMutation({
    mutationFn: (paymentId) => api.post(
      `/church/${churchId}/pledges/${pledge.id}/payments/${paymentId}/reverse`,
      { reason: 'reversed by the assembly' }),
    onSuccess: () => { toast.success('Payment reversed'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not reverse that payment')),
  });

  const payments = pledge.payments || [];

  return (
    <>
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Record a payment · {pledge.donor_name}</DialogTitle>
        </DialogHeader>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm"
             data-testid="payment-summary">
          {pledge.campaign_title ? (
            <p className="text-xs text-amber-700">{pledge.campaign_title}</p>
          ) : null}
          <p>Pledged <strong>{money(pledge.amount, pledge.currency)}</strong></p>
          <p>Paid <strong>{money(pledge.paid_amount, pledge.currency)}</strong></p>
          <p>Outstanding <strong>{money(pledge.remaining_amount, pledge.currency)}</strong></p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Amount received</Label>
            <Input type="number" value={form.amount} data-testid="payment-amount"
                   onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Currency</Label>
            <CurrencySelect
              value={form.currency}
              homeCurrency={pledge.currency}
              showHomeHint={false}
              testId="payment-currency"
              onChange={(c) => setForm((f) => ({ ...f, currency: c }))}
            />
          </div>
          <div>
            <Label className="text-xs">Method</Label>
            <Select value={form.payment_method}
                    onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}>
              <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => <SelectItem key={m} value={m}>{m.replace('_', ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Reference</Label>
            <Input value={form.reference} data-testid="payment-reference"
                   onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} />
          </div>
        </div>
        <Textarea rows={2} value={form.notes} placeholder="Notes (optional)"
                  data-testid="payment-notes"
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />

        {quote ? (
          quote.needs_rate ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm"
                 data-testid="payment-needs-rate">
              <p className="font-medium text-amber-900">
                An exchange rate is required to convert this contribution into{' '}
                {quote.pledge_currency}.
              </p>
              <p className="mt-1 text-xs text-amber-800">
                You can still record it. The {form.currency} amount is kept exactly as
                given and held out of the pledge balance until a finance approver
                supplies a rate — it will not be guessed at.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm"
                 data-testid="payment-conversion">
              <p>
                {money(quote.original_amount, quote.original_currency)} ={' '}
                <strong>{money(quote.converted_amount, quote.to_currency)}</strong>
                <span className="text-xs text-slate-500">
                  {' '}at {Number(quote.rate).toFixed(4)} · {(quote.rate_date || '').slice(0, 10)}
                </span>
              </p>
              <p className="text-xs text-slate-500">
                Outstanding after this payment:{' '}
                {money(quote.outstanding_after, quote.pledge_currency)}
                {quote.rate_source ? ` · rate from ${quote.rate_source}` : ''}
              </p>
            </div>
          )
        ) : null}

        {payments.length ? (
          <div data-testid="payment-history">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
              <Receipt className="h-3 w-3" />Payment history
            </p>
            <ul className="mt-1.5 space-y-1">
              {payments.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                  <span className={p.reversed_at ? 'line-through' : ''}>
                    {money(p.amount, p.currency || pledge.currency)}
                  </span>
                  {p.conversion_status === 'pending_rate' ? (
                    <Badge variant="outline" className="border-amber-400 text-amber-700"
                           data-testid={`payment-awaiting-rate-${p.id}`}>
                      awaiting rate
                    </Badge>
                  ) : null}
                  <span className="text-xs text-slate-400">
                    {(p.recorded_at || '').slice(0, 10)} · {p.payment_method}
                    {p.reference ? ` · ${p.reference}` : ''}
                  </span>
                  {p.receipt_number ? (
                    <span className="font-mono text-xs text-slate-500"
                          data-testid={`receipt-no-${p.id}`}>{p.receipt_number}</span>
                  ) : null}
                  <Button size="sm" variant="ghost" className="h-7 text-xs"
                          onClick={() => setReceiptFor(p.id)}
                          data-testid={`payment-receipt-${p.id}`}>
                    <Printer className="mr-1 h-3 w-3" />Receipt
                  </Button>
                  {p.reversed_at ? (
                    <Badge variant="outline">reversed</Badge>
                  ) : (
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600"
                            disabled={reverse.isPending}
                            onClick={() => reverse.mutate(p.id)}
                            data-testid={`payment-reverse-${p.id}`}>
                      <Undo2 className="mr-1 h-3 w-3" />Reverse
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => pay.mutate()}
                  disabled={!(Number(form.amount) > 0) || pay.isPending}
                  data-testid="payment-save">
            {pay.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Record payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {receiptFor ? (
      <ReceiptDialog churchId={churchId} pledgeId={pledge.id} paymentId={receiptFor}
                     onClose={() => setReceiptFor(null)} />
    ) : null}
    </>
  );
};
